import React, { useEffect, useState, useContext } from "react";
import {
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Modal,
  Form,
  Badge,
} from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import HusariaHeader from "views/pages/components/HusariaHeader";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";

const PORT_STATUS = {
  ACTIVE: { label: "Active", variant: "success" },
  PORTED_OUT: { label: "Ported Out", variant: "danger" },
  UNKNOWN: { label: "Unknown", variant: "secondary" },
};

const InboundTable = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const title = "Inbound Routes";
  const addRoute = "/admin/subscription";
  const importRoute = "/admin/hucs/import-did";
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);

  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const canDelete = hasPermission(userData, PERMISSIONS.DELETE);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  const visibleColumns = [
    { key: "dialplan_name", label: "Name" },
    { key: "dialplan_number", label: "Number" },
    { key: "dialplan_context", label: "Context" },
    { key: "dialplan_order", label: "Order" },
    { key: "dialplan_enabled", label: "Enabled" },
    { key: "dialplan_description", label: "Description" },
    { key: "port_status", label: "Port Status" },
  ];

  const getPortBadge = (portStatus) => {
    const key = portStatus?.toUpperCase() ?? "UNKNOWN";
    const config = PORT_STATUS[key] ?? PORT_STATUS.UNKNOWN;
    return (
      <Badge bg={config.variant} style={{ color: "black" }}>
        {config.label}
      </Badge>
    );
  };

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (col.key === "port_status") return getPortBadge(cell);
      if (index === 0 && canEdit) {
        return (
          <span
            className="tr-row-name"
            style={{ cursor: "pointer" }}
            onClick={() =>
              navigate(`/admin/hucs/edit/inbound-routes/${row.dialplan_uuid}`)
            }
          >
            {cell ?? "-"}
          </span>
        );
      }
      return cell ?? "-";
    },
  }));

  if (canDelete) {
    columns.push({
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <Button
          size="sm"
          variant="danger"
          onClick={(e) => {
            e.stopPropagation();
            toggleDeleteModal(row);
          }}
        >
          Delete
        </Button>
      ),
    });
  }

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
      const site_uuid = currentSite?.site_uuid || null;
      const response = await fetch(
        `${serverHost}/fetch_dids/${encodeURIComponent(site_uuid)}`,
      );
      if (!response.ok) throw new Error("Failed to fetch inbound routes");
      const data = await response.json();
      setRows(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const filteredRows = React.useMemo(() => {
    if (!search) return rows;

    return rows.filter((row) =>
      visibleColumns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(search.toLowerCase()),
      ),
    );
  }, [rows, search, visibleColumns]);

  const toggleDeleteModal = (row = null) => {
    setRowToDelete(row);
    setDeleteModal(!deleteModal);
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    try {
      const res = await fetch(
        `${serverHost}/delete_route/${rowToDelete.dialplan_uuid}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );
      if (!res.ok) throw new Error("Delete failed");
      await res.json();
      toggleDeleteModal();
      fetchRows();
    } catch (err) {
      alert(`Error deleting row: ${err.message}`);
    }
  };

  const singularTitle = title.endsWith("s") ? title.slice(0, -1) : title;

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading inbound routes...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader name="Inbound Routes" parentName="HUCS" padding="4.5rem" />
      <Container fluid className="mt--6">
        <Card className={`shadow-sm ${isDark ? "bg-dark text-light" : ""}`}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
            className="d-flex justify-content-between align-items-center"
          >
            <div className="d-flex align-items-center">
              <Form className="navbar-search navbar-search-light form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge search-border">
                    <InputGroupText>
                      <i
                        className="fas fa-search search-icon"
                        style={{
                          color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)",
                        }}
                      />
                    </InputGroupText>
                    <Input
                      className="form-control table-search-bar"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>

            <div className="d-flex gap-2">
              <Button variant="info" size="sm" onClick={fetchRows}>
                Refresh
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate("/admin/hucs/add/inbound-routes")}
              >
                + Add {singularTitle}
              </Button>
              {(userData?.user_role === "GLOBAL") && (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(addRoute)}
                  >
                    + Order DID
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(importRoute)}
                  >
                    <i class="fas fa-file-import"></i> Import DID
                  </Button>
                </>
              )}
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div style={{ overflowX: "auto" }}>
              <ToolkitProvider
                keyField="dialplan_uuid"
                data={filteredRows}
                columns={columns}
                search
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <BootstrapTable
                      {...props.baseProps}
                      bootstrap4
                      pagination={pagination}
                      bordered={false}
                      hover
                      classes="align-items-center table-flush"
                      rowEvents={{
                        onClick: (_, row) => {
                          if (canEdit) {
                            navigate(
                              `/admin/hucs/edit/inbound-routes/${row.dialplan_uuid}`,
                            );
                          }
                        },
                      }}
                      rowStyle={{ cursor: "pointer" }}
                      noDataIndication={() =>
                        loading ? (
                          <div className="text-center">Loading...</div>
                        ) : (
                          <div className="text-center">
                            No inbound routes found.
                          </div>
                        )
                      }
                    />
                  </div>
                )}
              </ToolkitProvider>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            Delete {singularTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          Are you sure you want to delete this {singularTitle}? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#111827" : "#f8f9fa",
          }}
        >
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InboundTable;
